import React, { useEffect, useState } from "react";
import {
  FaCheckCircle,
  FaHourglassHalf,
  FaPhoneAlt,
  FaCalendarAlt,
  FaThList,
  FaBuilding,
  FaTable,
} from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchPartyCallDetails } from "../../Redux/Slice/Partycalldetails";
import Loader from "../../Components/Loader/Loader";
import PartyCallCalendarView from "./Components/PartyCallCalendarView";

const categories = [
  "ALL",
  "AGENCY",
  "PROCESS HOUSE",
  "TRADING",
  "WEAVING",
  "OTHER",
];

const Partycalldetails = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const viewParam = searchParams.get("view");

  const { Partydatacard, partyCallListLoading } = useSelector(
    (state) => state.partyCall
  );

  const [filteredData, setFilteredData] = useState([]);
  const [priorityData, setPriorityData] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("ALL");
  const [categoryCounts, setCategoryCounts] = useState({});

  // View state: sync with URL param ?view=calendar or default to "current"
  const [viewOption, setViewOption] = useState(() => {
    if (viewParam === "calendar") return "calendar";
    return "current";
  });

  useEffect(() => {
    if (viewParam === "calendar") {
      setViewOption("calendar");
    } else if (viewParam === "current" || !viewParam) {
      setViewOption("current");
    }
  }, [viewParam]);

  useEffect(() => {
    dispatch(fetchPartyCallDetails());
  }, [dispatch]);

  const getItemType = (item) => {
    const t = (item.Type || "").trim().toUpperCase();
    if (!t || t === "-" || t === "NULL" || t === "UNDEFINED") return "OTHER";
    return t;
  };

  useEffect(() => {
    if (Partydatacard?.result) {
      const enriched = Partydatacard.result.map((item) => ({
        ...item,
      }));

      const validCompanies = enriched.filter((item) => item.CompanyName);
      const sorted = validCompanies.sort(
        (a, b) => (b.pending || 0) - (a.pending || 0)
      );

      const matchesCategory = (item) => {
        if (selectedCategory === "ALL") return true;
        return getItemType(item) === selectedCategory;
      };

      const priorityList = sorted.filter(
        (item) => item.Priority?.toLowerCase() === "on" && matchesCategory(item)
      );

      const restList = sorted.filter(
        (item) =>
          item.Priority?.toLowerCase() !== "on" && matchesCategory(item)
      );

      setPriorityData(priorityList);
      setFilteredData(restList);

      const counts = { ALL: validCompanies.length };
      categories.forEach((cat) => {
        if (cat !== "ALL") counts[cat] = 0;
      });
      validCompanies.forEach((item) => {
        const type = getItemType(item);
        counts[type] = (counts[type] || 0) + 1;
      });
      setCategoryCounts(counts);
    }
  }, [Partydatacard, selectedCategory]);

  const handleViewClick = (cardId, detailsType) => {
    navigate(`/partycalldetails/${cardId}?details=${detailsType}`);
  };

  if (partyCallListLoading || !Partydatacard || !Partydatacard?.result) {
    return <Loader />;
  }

  return (
    <div className="px-1 pt-0 pb-4 md:px-2 md:pb-6 space-y-3">
      {/* Sleek, Compact Top Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2.5 bg-white px-3.5 py-2.5 rounded-xl border border-slate-200/80 shadow-2xs">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-[#154D75] text-white flex items-center justify-center shadow-xs shrink-0">
            <FaPhoneAlt className="text-xs" />
          </div>
          <div className="flex items-center gap-2 min-w-0">
            <h1 className="text-sm sm:text-base font-bold text-slate-800 tracking-tight truncate">
              Party Call Details
            </h1>
            <span className="text-[11px] font-bold text-[#154D75] bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full shrink-0">
              {Partydatacard?.result?.length || 0} Parties
            </span>
          </div>
        </div>

        {/* View Options Toggle: Cards View | Table View | Calendar View */}
        <div className="inline-flex shrink-0 p-0.5 rounded-lg bg-slate-100 border border-slate-200 shadow-inner">
          <button
            type="button"
            onClick={() => {
              setViewOption("current");
              navigate("/partycalldetails");
            }}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md font-bold text-xs transition-all duration-200 cursor-pointer whitespace-nowrap select-none ${
              viewOption === "current"
                ? "bg-[#154D75] text-white shadow-xs"
                : "text-slate-600 hover:text-[#154D75] hover:bg-white/80"
            }`}
            title="Switch to Cards View"
          >
            <FaThList className="text-[11px]" />
            <span>Cards View</span>
          </button>

          <button
            type="button"
            onClick={() => navigate("/partycalldetails/all")}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md font-bold text-xs text-slate-600 hover:text-[#154D75] hover:bg-white/80 transition-all duration-200 cursor-pointer whitespace-nowrap select-none"
            title="Open Master Table View (All Calls)"
          >
            <FaTable className="text-[11px]" />
            <span>Table View</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setViewOption("calendar");
              navigate("/partycalldetails?view=calendar");
            }}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md font-bold text-xs transition-all duration-200 cursor-pointer whitespace-nowrap select-none ${
              viewOption === "calendar"
                ? "bg-[#154D75] text-white shadow-xs"
                : "text-slate-600 hover:text-[#154D75] hover:bg-white/80"
            }`}
            title="Switch to Calendar View"
          >
            <FaCalendarAlt className="text-[11px]" />
            <span>Calendar View</span>
          </button>
        </div>
      </div>

      {viewOption === "calendar" ? (
        /* Calendar View */
        <PartyCallCalendarView parties={Partydatacard?.result || []} />
      ) : (
        /* Current View - Original Structure with Proper Design */
        <div className="space-y-4">
          {/* Category Filter Buttons + Master Table View CTA */}
          <div className="flex flex-wrap items-center justify-between gap-2 pt-0.5">
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
              {categories.map((cat, index) => {
                const isSelected = selectedCategory === cat;
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-2.5 py-1 rounded-lg font-bold text-xs transition-all duration-150 flex items-center gap-1.5 justify-center cursor-pointer border shadow-2xs active:scale-95 ${
                      isSelected
                        ? "text-white border-[#154D75]"
                        : "text-[#154D75] bg-[#EAF3FA] border-[#B6D3EA] hover:bg-[#D6E9F7] hover:shadow-xs"
                    }`}
                    style={{
                      backgroundColor: isSelected ? "#154D75" : undefined,
                    }}
                  >
                    <span>{cat}</span>
                    <span
                      className={`text-[10px] font-bold px-1.5 py-0.2 rounded-full ${
                        isSelected
                          ? "bg-white/20 text-white"
                          : "bg-[#CFE4F5] text-[#154D75]"
                      }`}
                    >
                      {categoryCounts[cat] || 0}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Master Table View CTA Button */}
            <button
              type="button"
              onClick={() => navigate("/partycalldetails/all")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#154D75] hover:bg-[#0f3754] text-white font-bold text-xs shadow-xs transition-all cursor-pointer select-none"
              title="Open all customer requests in dedicated Master Table View"
            >
              <FaTable className="text-[11px]" />
              <span>Master Table View (All Calls) →</span>
            </button>
          </div>

          {/* Priority Companies Section */}
          {priorityData.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-center gap-2">
                <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse"></span>
                <h4 className="text-xs sm:text-sm font-bold text-red-600 tracking-wide uppercase">
                  Priority Companies
                </h4>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3.5 sm:gap-4">
                {priorityData.map((data) => (
                  <CompanyInfoCard
                    key={data._id}
                    data={data}
                    isPriority={true}
                    onViewClick={handleViewClick}
                  />
                ))}
              </div>
            </div>
          )}

          {/* All Other Companies Section */}
          {filteredData.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3.5 sm:gap-4 pt-1">
              {filteredData.map((data) => (
                <CompanyInfoCard
                  key={data._id}
                  data={data}
                  isPriority={false}
                  onViewClick={handleViewClick}
                />
              ))}
            </div>
          ) : priorityData.length === 0 ? (
            <p className="text-center text-slate-500 text-xs sm:text-sm mt-6">
              No companies found for the selected category.
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
};

const CompanyInfoCard = ({ data, isPriority, onViewClick }) => {
  const targetId = data._id || data.CompanyId;

  return (
    <div
      onClick={() => onViewClick(targetId, "Total")}
      className={`p-3 sm:p-3.5 md:p-4 rounded-xl transition-all duration-200 flex flex-col justify-between h-full border cursor-pointer select-none group hover:-translate-y-0.5 ${
        isPriority
          ? "bg-blue-50/70 border-red-500 shadow-xs hover:shadow-md hover:border-red-600"
          : "bg-white border-slate-300 shadow-xs hover:shadow-md hover:border-[#154D75]"
      }`}
      title={`Click to view call details for ${data.CompanyName}`}
    >
      {/* Company Header */}
      <div className="flex items-center gap-2 sm:gap-2.5 mb-2.5 pb-2 border-b border-slate-200/60 min-w-0">
        <div className="w-6 h-6 rounded-md bg-[#122A47] text-white flex items-center justify-center shrink-0 shadow-xs group-hover:bg-[#154D75] transition-colors">
          <FaBuilding className="text-[11px]" />
        </div>
        <h2
          className="text-xs sm:text-sm font-bold text-slate-900 truncate leading-snug flex-1 min-w-0 group-hover:text-[#154D75] transition-colors"
          title={data.CompanyName}
        >
          {data.CompanyName}
        </h2>
        {isPriority && (
          <span className="text-[10px] font-bold text-red-600 bg-red-100 border border-red-200 px-1.5 py-0.2 rounded shrink-0">
            Priority
          </span>
        )}
      </div>

      {/* Stat Rows */}
      <div className="space-y-1.5 sm:space-y-2 min-w-0">
        <StatRow
          icon={<FaPhoneAlt className="text-slate-500 text-xs shrink-0" />}
          label="TOTAL CALLS"
          count={data.total || 0}
          color="bg-[#122A47] hover:bg-[#0a1b30]"
          textColor="text-white"
          onClick={() => onViewClick(targetId, "Total")}
        />

        <StatRow
          icon={<FaCheckCircle className="text-green-600 text-xs shrink-0" />}
          label="COMPLETED"
          count={data.completed || 0}
          color="bg-green-600 hover:bg-green-700"
          textColor="text-white"
          onClick={() => onViewClick(targetId, "Completed")}
        />

        <StatRow
          icon={<FaHourglassHalf className="text-yellow-500 text-xs shrink-0" />}
          label="PENDING"
          count={data.pending || 0}
          color="bg-yellow-400 hover:bg-yellow-500"
          textColor="text-black"
          onClick={() => onViewClick(targetId, "Pending")}
        />
      </div>
    </div>
  );
};

const StatRow = ({ icon, label, count, onClick, color, textColor }) => (
  <div className="flex justify-between items-center py-0.5 gap-2 min-w-0">
    <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-[13px] font-medium text-slate-800 min-w-0 truncate">
      {icon}
      <p className="tracking-tight truncate">
        {label}: <span className="font-bold text-slate-900">{count}</span>
      </p>
    </div>
    {count > 0 ? (
      <button
        type="button"
        className={`text-[10px] sm:text-[11px] font-bold ${color} ${textColor} px-2 sm:px-2.5 py-0.5 rounded shadow-2xs transition-all cursor-pointer tracking-wider hover:opacity-90 active:scale-95 shrink-0`}
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
      >
        VIEW
      </button>
    ) : (
      <span className="text-xs font-semibold text-slate-300 px-2 py-0.5 select-none shrink-0">
        -
      </span>
    )}
  </div>
);

export default Partycalldetails;
